import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { Check, X, User, Building, Calendar, Phone, Mail, Landmark, DollarSign, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../Sidebar/Sidebar';

function ProofsManagementPage() {
    const [selectedTab, setSelectedTab] = useState(0);
    const [pendingProofs, setPendingProofs] = useState([]);
    const [rejectedProofs, setRejectedProofs] = useState([]);
    const [verifiedProofs, setVerifiedProofs] = useState([]);
    const [loading, setLoading] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };



    useEffect(() => {
        const fetchAllProofs = async () => {
            try {
                const [pendingRes, rejectedRes, verifiedRes] = await Promise.all([
                    fetch(
                        // 'http://localhost:3005/admin/pending-proofs'
                        'https://portal-sanieldanproperties-api.onrender.com/admin/pending-proofs'
                        ),
                    fetch(
                        // 'http://localhost:3005/admin/rejected-proofs'
                        'https://portal-sanieldanproperties-api.onrender.com/admin/rejected-proofs'
                        ),
                    fetch(
                        // 'http://localhost:3005/admin/verified-proofs'
                        'https://portal-sanieldanproperties-api.onrender.com/admin/rejected-proofs'
                        )
                ]);

                const [pending, rejected, verified] = await Promise.all([
                    pendingRes.json(),
                    rejectedRes.json(),
                    verifiedRes.json()
                ]);

                setPendingProofs(pending);
                setRejectedProofs(rejected);
                setVerifiedProofs(verified);
            } catch (error) {
                showToast('Error fetching proofs', 'error');
                console.error('Error fetching proofs:', error);
            }
        };

        fetchAllProofs();
    }, []);

    const handleApprove = async (proofId) => {
        setLoading(prev => ({ ...prev, [proofId]: true }));
        try {
            await fetch(
                // `http://localhost:3005/admin/proofs/${proofId}/approve`
                `https://portal-sanieldanproperties-api.onrender.com/admin/proofs/${proofId}/approve`
                , {
                method: 'PATCH'
            });
            showToast('Proof approved successfully');
            setPendingProofs(pendingProofs.filter(proof => proof._id !== proofId));
        } catch (error) {
            showToast('Error approving proof', 'error');
        } finally {
            setLoading(prev => ({ ...prev, [proofId]: false }));
        }
    };

    const handleReject = async (proofId) => {
        setLoading(prev => ({ ...prev, [proofId]: true }));
        try {
            await fetch(
                // `http://localhost:3005/admin/proofs/${proofId}/reject`
                `https://portal-sanieldanproperties-api.onrender.com/admin/proofs/${proofId}/reject`
                , {
                method: 'PATCH'
            });
            setPendingProofs(pendingProofs.filter(proof => proof._id !== proofId));
            showToast('Proof rejected successfully');
        } catch (error) {
            showToast('Error rejecting proof', 'error');
        } finally {
            setLoading(prev => ({ ...prev, [proofId]: false }));
        }
    };

    const Toast = () => (
        <AnimatePresence>
            {toast.show && (
                <motion.div
                    initial={{ opacity: 0, y: -100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -100 }}
                    className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
                        toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
                    } text-white`}
                >
                    {toast.message}
                </motion.div>
            )}
        </AnimatePresence>
    );

    const ProofCard = ({ proof, type }) => (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    {/* Realtor Details Section */}
                    <div className="border-b pb-4">
                        <h3 className="text-lg font-semibold text-blue-600 mb-3">Realtor Details</h3>
                        <div className="flex items-center space-x-2">
                            <User className="text-gray-500" />
                            <div>
                                <p className="text-sm text-gray-500">Realtor Name</p>
                                <p className="font-medium">
                                    {proof.userDetails.firstName} {proof.userDetails.lastName}
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 mt-3">
                            <Phone className="text-gray-500" />
                            <div>
                                <p className="text-sm text-gray-500">Phone</p>
                                <p className="font-medium">{proof.userDetails.phoneNumber}</p>
                            </div>
                        </div>
    
                        <div className="flex items-center space-x-2 mt-3">
                            <Mail className="text-gray-500" />
                            <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium">{proof.userDetails.email}</p>
                            </div>
                        </div>
    
                        <div className="flex items-center space-x-2 mt-3">
                            <Landmark className="text-gray-500" />
                            <div>
                                <p className="text-sm text-gray-500">Bank Details</p>
                                <p className="font-medium">
                                    {proof.userDetails.bankFullName}
                                </p>
                                <p className="text-sm">
                                    {proof.userDetails.bankName} - {proof.userDetails.bankNumber}
                                </p>
                            </div>
                        </div>
                    </div>
    
                    {/* Client Details Section - Only shown if client exists */}
                    {proof.client && (
                        <div className="border-b pb-4">
                            <h3 className="text-lg font-semibold text-blue-600 mb-3">Client Details</h3>
                            <div className="flex items-center space-x-2">
                                <User className="text-gray-500" />
                                <div>
                                    <p className="text-sm text-gray-500">Client Name</p>
                                    <p className="font-medium">
                                        {proof.client.firstName} {proof.client.lastName}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center space-x-2 mt-3">
                                <Phone className="text-gray-500" />
                                <div>
                                    <p className="text-sm text-gray-500">Phone</p>
                                    <p className="font-medium">{proof.client.phone}</p>
                                </div>
                            </div>
    
                            <div className="flex items-center space-x-2 mt-3">
                                <Mail className="text-gray-500" />
                                <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="font-medium">{proof.client.email}</p>
                                </div>
                            </div>
                        </div>
                    )}
    
                    {/* Property Details Section */}
                    <div>
                        <h3 className="text-lg font-semibold text-blue-600 mb-3">Property Details</h3>
                        <div className="flex items-center space-x-2">
                            <Building className="text-gray-500" />
                            <div>
                                <p className="text-sm text-gray-500">Property</p>
                                <p className="font-medium">{proof.propertyBought}</p>
                            </div>
                        </div>
    
                        <div className="flex items-center space-x-2 mt-3">
                            <DollarSign className="text-gray-500" />
                            <div>
                                <p className="text-sm text-gray-500">Transaction Details</p>
                                <p className="font-medium">
                                    Price: ₦{proof.propertyPrice}
                                </p>
                                <p className="font-medium">
                                    Units: {proof.noOfUnits}
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Status Information */}
                    {proof.rejectionReason && (
                        <div className="mt-4 p-3 bg-red-50 rounded-lg">
                            <p className="text-sm text-gray-500">Rejection Reason:</p>
                            <p className="text-red-600">{proof.rejectionReason}</p>
                        </div>
                    )}
                    
                    {proof.verifiedAt && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg">
                            <p className="text-sm text-gray-500">Verified At:</p>
                            <p className="text-green-600">
                                {new Date(proof.verifiedAt).toLocaleDateString()}
                            </p>
                        </div>
                    )}
                </div>
    
                {/* Right Column - Proof Image and Actions */}
                <div className="space-y-4">
                    <div className="border rounded-lg p-2">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Proof</h4>
                        <img 
                            src={proof.imageUrl} 
                            alt="Proof" 
                            className="w-full h-64 object-contain rounded-lg"
                        />
                    </div>
                    
                    {type === 'pending' && (
                        <div className="flex justify-end space-x-2">
                            <button 
                                onClick={() => handleApprove(proof._id)}
                                disabled={loading[proof._id]}
                                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading[proof._id] ? (
                                    <Loader className="animate-spin h-4 w-4" />
                                ) : (
                                    <Check size={16} />
                                )}
                                <span>Approve</span>
                            </button>
                            <button 
                                onClick={() => handleReject(proof._id)}
                                disabled={loading[proof._id]}
                                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading[proof._id] ? (
                                    <Loader className="animate-spin h-4 w-4" />
                                ) : (
                                    <X size={16} />
                                )}
                                <span>Reject</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
         <Toast />
            <div className="home_sidebar">
                <Sidebar />
            </div>
            <div className="p-6 max-w-7xl mx-auto ml-60">
                <h1 className="text-3xl font-bold mb-8">Proof Management</h1>
                
                <Tab.Group onChange={setSelectedTab}>
                    <Tab.List className="flex space-x-2 rounded-xl bg-gray-100 p-1 mb-6">
                        <Tab className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                            ${selected 
                                ? 'bg-white shadow text-blue-600' 
                                : 'text-gray-600 hover:bg-white/[0.12] hover:text-blue-600'
                            }`
                        }>
                            Pending ({pendingProofs.length})
                        </Tab>
                        <Tab className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                            ${selected 
                                ? 'bg-white shadow text-blue-600' 
                                : 'text-gray-600 hover:bg-white/[0.12] hover:text-blue-600'
                            }`
                        }>
                            Verified ({verifiedProofs.length})
                        </Tab>
                        <Tab className={({ selected }) =>
                            `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                            ${selected 
                                ? 'bg-white shadow text-blue-600' 
                                : 'text-gray-600 hover:bg-white/[0.12] hover:text-blue-600'
                            }`
                        }>
                            Rejected ({rejectedProofs.length})
                        </Tab>
                    </Tab.List>

                    <Tab.Panels>
                        <Tab.Panel>
                            <div className="space-y-4">
                                {pendingProofs.map(proof => (
                                    <ProofCard key={proof._id} proof={proof} type="pending" />
                                ))}
                                {pendingProofs.length === 0 && (
                                    <p className="text-center text-gray-500">No pending proofs</p>
                                )}
                            </div>
                        </Tab.Panel>
                        <Tab.Panel>
                            <div className="space-y-4">
                                {verifiedProofs.map(proof => (
                                    <ProofCard key={proof._id} proof={proof} type="verified" />
                                ))}
                                {verifiedProofs.length === 0 && (
                                    <p className="text-center text-gray-500">No verified proofs</p>
                                )}
                            </div>
                        </Tab.Panel>
                        <Tab.Panel>
                            <div className="space-y-4">
                                {rejectedProofs.map(proof => (
                                    <ProofCard key={proof._id} proof={proof} type="rejected" />
                                ))}
                                {rejectedProofs.length === 0 && (
                                    <p className="text-center text-gray-500">No rejected proofs</p>
                                )}
                            </div>
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </>
    );
}

export default ProofsManagementPage;